import { materials as staticMaterials } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import CTAStrip from "../components/CTAStrip";
import EmptyState from "../components/ui/EmptyState";
import PublicCardsSkeleton from "../components/ui/PublicCardsSkeleton";
import { pub } from "../lib/pubThemeClasses";
import { pickMaterialDisplay } from "../lib/cmsI18n";

const TABLE_ROWS = [
  { mat: "PLA", heat: "60°C", id: "pla" },
  { mat: "PETG", heat: "80°C", id: "petg" },
  { mat: "ABS", heat: "105°C", id: "abs" },
  { mat: "Reçine", heat: "—", id: "resin" },
  { mat: "Nylon (PA12)", heat: "170°C", id: "nylon" },
  { mat: "TPU", heat: "80°C", id: "tpu" },
];

export default function Materials() {
  const { materials, loaded } = usePublicData();
  const { t, language } = useLanguage();
  const cmsEmpty = loaded && Array.isArray(materials) && materials.length === 0;
  const MA = cmsEmpty ? [] : materials?.length ? materials : staticMaterials;
  const showSkeleton = !loaded && !cmsEmpty;

  return (
    <div>
      <section className={`relative border-b bg-grain ${pub.borderSubtle}`}>
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[#FF4F00]" /> {t("materials.eyebrow")}
          </div>
          <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight leading-[0.9] max-w-4xl ${pub.heroHeading}`}>
            {t("materials.hero.line1")}
            <br />
            <span className="text-[#FF4F00]">{t("materials.hero.highlight")}</span>
          </h1>
          <p className={`mt-6 text-lg max-w-2xl ${pub.muted}`}>{t("materials.hero.sub")}</p>
        </div>
      </section>

      <section className={`border-b ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          {showSkeleton ? (
            <PublicCardsSkeleton />
          ) : cmsEmpty || MA.length === 0 ? (
            <EmptyState description={t("materials.empty.desc")} actionLabel={t("materials.empty.home")} actionTo="/" />
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-hidden rounded-sm border ${pub.gridHairline} ${pub.borderSubtle}`}>
              {MA.map((m, i) => {
                const md = pickMaterialDisplay(m, language);
                return (
                <div key={m.key || m.id || i} className="bg-surface-2 p-8 group hover:bg-surface-3 transition-colors">
                  <div className="flex items-baseline justify-between mb-8">
                    <span className={`font-mono text-xs uppercase tracking-[0.25em] ${pub.mutedStr}`}>#{String(i + 1).padStart(2, "0")}</span>
                    <span className={`font-mono text-xs uppercase tracking-[0.2em] ${m.in_stock === false ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-[#00FF9D]"}`}>
                      {m.in_stock === false ? t("materials.stock.out") : t("materials.stock.in")}
                    </span>
                  </div>
                  <h3 className={`font-display text-3xl md:text-4xl font-black uppercase tracking-tight mb-4 group-hover:text-[#FF4F00] transition-colors ${pub.heroHeading}`}>{md.name}</h3>
                  <p className={`text-sm leading-relaxed mb-6 ${pub.muted}`}>{md.desc}</p>
                  <div className={`pt-5 border-t font-mono text-xs uppercase tracking-wide ${pub.borderSubtle} ${pub.mutedStr}`}>{m.spec}</div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className={`border-b bg-surface-1 ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] mb-4">{t("materials.compareTitle")}</div>
          <h2 className={`font-display text-3xl md:text-5xl font-black uppercase tracking-tight mb-10 ${pub.heroHeading}`}>{t("materials.compareSubtitle")}</h2>
          <div className={`rounded-sm overflow-x-auto border overflow-hidden ${pub.softPanel}`}>
            <table className="w-full text-sm font-mono">
              <thead className={`bg-surface-2 border-b ${pub.borderSubtle}`}>
                <tr className={`text-left uppercase text-xs tracking-[0.2em] ${pub.mutedStr}`}>
                  <th className="p-4">{t("materials.table.mat")}</th>
                  <th className="p-4">{t("materials.table.use")}</th>
                  <th className="p-4">{t("materials.table.heat")}</th>
                  <th className="p-4">{t("materials.table.strength")}</th>
                  <th className="p-4">{t("materials.table.detail")}</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((r) => (
                  <tr key={r.mat} className={`border-b hover:bg-orange-500/[0.04] dark:hover:bg-white/[0.04] ${pub.borderSubtle}`}>
                    <td className={`p-4 font-display text-base font-bold uppercase ${pub.heroHeading}`}>{r.mat}</td>
                    <td className={`p-4 ${pub.muted}`}>{t(`materials.row.${r.id}.use`)}</td>
                    <td className={`p-4 ${pub.muted}`}>{r.heat}</td>
                    <td className={`p-4 ${pub.muted}`}>{t(`materials.row.${r.id}.strength`)}</td>
                    <td className={`p-4 ${pub.muted}`}>{t(`materials.row.${r.id}.detail`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
